import { useState } from 'react'
import { useEmailQAStore, getCurrentReviewer } from '@/stores/email-qa'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { JsonView, allExpanded, darkStyles, defaultStyles } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'
import {
  Mail,
  ChevronDown,
  ChevronUp,
  Star,
  ThumbsUp,
  MessageSquare,
  Calendar
} from 'lucide-react'
import type { Database } from '@/types/database'

type EmailQAReview = Database['public']['Tables']['email_qa_reviews']['Row']

interface QAReviewCardProps {
  review: EmailQAReview
  currentReviewer?: string
}

export function QAReviewCard({ review, currentReviewer = 'Admin' }: QAReviewCardProps) {
  const { rateReview, approveReview } = useEmailQAStore()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isRating, setIsRating] = useState(false)
  const [notes, setNotes] = useState(review.notes || '')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500'
      case 'reviewed': return 'bg-blue-500'
      case 'approved': return 'bg-green-500'
      case 'needs_improvement': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Ausstehend'
      case 'reviewed': return 'Bewertet'
      case 'approved': return 'Genehmigt'
      case 'needs_improvement': return 'Verbesserung nötig'
      default: return status
    }
  }

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-600'
    if (rating >= 6) return 'text-yellow-600'
    if (rating >= 4) return 'text-orange-600'
    return 'text-red-600'
  }

  const handleQuickRate = async (rating: number) => {
    // KISS: Save directly to Supabase (skip API endpoint)
    try {
      const { supabase } = await import('@/lib/supabase')

      const { error } = await supabase
        .from('email_qa_reviews')
        .update({
          rating,
          notes: notes || null,
          reviewer: currentReviewer,
          status: 'reviewed'
        })
        .eq('id', review.id)

      if (error) {
        throw error
      }

      console.log(`✅ KISS: Rating saved directly - ${currentReviewer} rated ${review.id} with ${rating} stars`)

      // Refresh local data
      await rateReview(review.id, rating, notes)
    } catch (error) {
      console.error('Error saving QA rating:', error)
    }

    setIsRating(false)
  }

  const handleApprove = async () => {
    await approveReview(review.id, currentReviewer)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE')
  }

  // Parse JSON safely
  const parseJSON = (jsonString: string | null) => {
    if (!jsonString) return null
    try {
      return JSON.parse(jsonString)
    } catch {
      return null
    }
  }

  const extractorData = parseJSON(review.extractor_output)
  const vectorData = parseJSON(review.vector_output)

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div
          className="flex items-start justify-between gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 -m-3 p-3 rounded-md transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-500">
                {review.session_id ? `Session: ${review.session_id}` : 'Direkte Eingabe'}
              </span>
              <Calendar className="h-4 w-4 text-gray-500 ml-2" />
              <span className="text-sm text-gray-500">
                {formatDate(review.created_at)}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
              {review.original_email.substring(0, 150)}...
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {review.rating && (
              <Badge variant="outline" className={getRatingColor(review.rating)}>
                <Star className="h-3 w-3 mr-1 fill-current" />
                {review.rating}/10
              </Badge>
            )}
            <Badge
              variant="secondary"
              className={`text-white ${getStatusColor(review.status)}`}
            >
              {getStatusLabel(review.status)}
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Two Column Layout on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Original Email + Final Response */}
            <div className="space-y-4">
              {/* Original Email */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-blue-50 dark:bg-blue-950 px-4 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📧</span>
                    <span className="font-semibold text-blue-900 dark:text-blue-100">Original Email</span>
                  </div>
                </div>
                <div className="p-4 bg-white dark:bg-gray-900 max-h-96 overflow-y-auto">
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap text-sm">
                    {review.original_email}
                  </div>
                </div>
              </div>

              {/* Final Response */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-green-50 dark:bg-green-950 px-4 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📄</span>
                    <span className="font-semibold text-green-900 dark:text-green-100">Final Response</span>
                  </div>
                </div>
                <div className="p-4 bg-white dark:bg-gray-900 max-h-96 overflow-y-auto">
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: review.final_response }}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Agent Pipeline Outputs */}
            <div className="space-y-4">
              {/* Question Extractor */}
              {review.extractor_output && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-purple-50 dark:bg-purple-950 px-4 py-2 border-b">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🔍</span>
                      <span className="font-semibold text-purple-900 dark:text-purple-100">Question Extractor</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white dark:bg-gray-900 max-h-64 overflow-y-auto">
                    {extractorData ? (
                      <JsonView
                        data={extractorData}
                        shouldExpandNode={allExpanded}
                        style={defaultStyles}
                      />
                    ) : (
                      <pre className="text-xs whitespace-pre-wrap">{review.extractor_output}</pre>
                    )}
                  </div>
                </div>
              )}

              {/* Email Styler */}
              {review.styler_output && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-teal-50 dark:bg-teal-950 px-4 py-2 border-b">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🎨</span>
                      <span className="font-semibold text-teal-900 dark:text-teal-100">Email Styler (Bernhard)</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white dark:bg-gray-900 max-h-64 overflow-y-auto">
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: review.styler_output }}
                    />
                  </div>
                </div>
              )}

              {/* Refiner - Collapsed by default */}
              {review.refiner_output && (
                <details className="border rounded-lg overflow-hidden group">
                  <summary className="bg-orange-50 dark:bg-orange-950 px-4 py-2 border-b cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900 transition-colors flex items-center gap-2 list-none">
                    <ChevronDown className="h-4 w-4 text-orange-600 transition-transform group-open:rotate-180" />
                    <span className="text-lg">🔧</span>
                    <span className="font-semibold text-orange-900 dark:text-orange-100">
                      Refiner ({review.refiner_type})
                    </span>
                  </summary>
                  <div className="p-4 bg-white dark:bg-gray-900 max-h-64 overflow-y-auto">
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: review.refiner_output }}
                    />
                  </div>
                </details>
              )}

              {/* Vector Output - Collapsed JSON */}
              {review.vector_output && (
                <details className="border rounded-lg overflow-hidden group">
                  <summary className="bg-indigo-50 dark:bg-indigo-950 px-4 py-2 border-b cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors flex items-center gap-2 list-none">
                    <ChevronDown className="h-4 w-4 text-indigo-600 transition-transform group-open:rotate-180" />
                    <span className="text-lg">🔢</span>
                    <span className="font-semibold text-indigo-900 dark:text-indigo-100">
                      Vector Search Results
                    </span>
                  </summary>
                  <div className="p-4 bg-white dark:bg-gray-900 max-h-64 overflow-y-auto">
                    {vectorData ? (
                      <JsonView
                        data={vectorData}
                        shouldExpandNode={allExpanded}
                        style={defaultStyles}
                      />
                    ) : (
                      <pre className="text-xs whitespace-pre-wrap">{review.vector_output}</pre>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>

          {/* Review Notes */}
          {review.notes && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800 dark:text-blue-200">Notizen</span>
                {review.reviewer && (
                  <span className="text-sm text-blue-600 dark:text-blue-300">von {review.reviewer}</span>
                )}
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">{review.notes}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {/* Quick Rating */}
            {review.status === 'pending' && !isRating && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">Schnell bewerten:</span>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <Button
                    key={rating}
                    variant="outline"
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => handleQuickRate(rating)}
                  >
                    {rating}
                  </Button>
                ))}
              </div>
            )}

            {/* Detailed Rating */}
            {!isRating && review.status === 'pending' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRating(true)}
              >
                <Star className="h-4 w-4 mr-1" />
                Detailliert bewerten
              </Button>
            )}

            {/* Approve */}
            {review.status === 'reviewed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleApprove}
                className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950"
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                Genehmigen
              </Button>
            )}
          </div>

          {/* Detailed Rating Form */}
          {isRating && (
            <div className="p-4 border rounded-lg bg-white dark:bg-gray-900">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Bewertung (1-10)</label>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                      <Button
                        key={rating}
                        variant="outline"
                        size="sm"
                        className="w-10 h-10"
                        onClick={async () => {
                          await rateReview(review.id, rating, notes)
                          setIsRating(false)
                        }}
                      >
                        {rating}
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Notizen</label>
                  <Textarea
                    placeholder="Feedback und Verbesserungsvorschläge..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsRating(false)}
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}